// Field photos: API client and session for crew members, who sign in with their crew
// phone number and their own PIN (set by an admin in Setup > Crews) instead of a CRM account.

export type FieldDepartment = 'mitigation' | 'recon' | 'packout'

export const FIELD_DEPARTMENTS: { value: FieldDepartment; label: string }[] = [
  { value: 'mitigation', label: 'Mitigation' },
  { value: 'recon', label: 'Recon' },
  { value: 'packout', label: 'Packout' },
]

// The label in the worker's language (the English `label` above is the translation key).
export const departmentLabel = (value: string | null | undefined) => {
  const label = FIELD_DEPARTMENTS.find((item) => item.value === value)?.label
  return label ? ft(label) : ''
}

export interface FieldSession {
  gps_required?: boolean
  // The office turned on Sales (leads, estimates, contracts) for this worker.
  sales_enabled?: boolean
  crm_user_id?: number | null
  worker_id?: number | null
  crew_id?: number | null
  worker_name: string
  phone_number: string
  department: FieldDepartment
  allowed_departments: FieldDepartment[]
  crew: string | null
  organization: string
  expires_at: string
}

export interface FieldPlace {
  kind: 'project' | 'field_project'
  id: number
  title: string
  address: string
  status: string | null
  distance_meters: number | null
  cover_url: string | null
}

export interface FieldPhoto {
  id: number
  url: string
  name: string
  taken_at: string | null
  captured_by: string | null
  latitude: number | null
  longitude: number | null
  created_at: string
}

export interface FieldPlaceDetail {
  kind: 'project' | 'field_project'
  id: number
  project?: number | null
  title?: string
  address?: string
  note?: string
  cover_photo?: number | null
  cover_url?: string | null
  latitude?: number | null
  longitude?: number | null
  department?: FieldDepartment | null
  counts?: Partial<Record<FieldDepartment, number>>
  photos?: FieldPhoto[]
}

export interface FieldPosition {
  latitude: number
  longitude: number
  accuracy: number
}

export type FieldFetch = <T = unknown>(
  url: string,
  options?: {
    method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
    body?: Record<string, unknown> | FormData
    params?: Record<string, unknown>
  }
) => Promise<T>

const TOKEN_KEY = 'field-token'
const SESSION_KEY = 'field-session'
const CHOSEN_KEY = 'field-department-chosen'

const readStorage = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

const writeStorage = (key: string, value: string | null) => {
  try {
    if (value === null) localStorage.removeItem(key)
    else localStorage.setItem(key, value)
  } catch {
    // Private mode: the session lasts until the tab closes.
  }
}

export function useField() {
  const config = useRuntimeConfig()
  const token = useState<string | null>('field-token', () =>
    import.meta.client ? readStorage(TOKEN_KEY) : null
  )
  const session = useState<FieldSession | null>('field-session', () => {
    const raw = import.meta.client ? readStorage(SESSION_KEY) : null
    try {
      const restored = raw ? (JSON.parse(raw) as FieldSession) : null
      // Older Job Photos installs saved the session before department access existed.
      // Refresh /me on mount for current access without blocking the employee home.
      if (restored && !Array.isArray(restored.allowed_departments)) {
        restored.allowed_departments = []
      }
      return restored
    } catch {
      return null
    }
  })

  const isSignedIn = computed(() => Boolean(token.value))
  const departmentChosen = useState<boolean>(
    'field-department-chosen',
    () => !import.meta.client || readStorage(CHOSEN_KEY) === '1'
  )

  const setSession = (
    newToken: string | null,
    newSession: FieldSession | null
  ) => {
    token.value = newToken
    session.value = newSession
    writeStorage(TOKEN_KEY, newToken)
    writeStorage(SESSION_KEY, newSession ? JSON.stringify(newSession) : null)
  }

  const rawFetch = $fetch.create({
    baseURL: `${config.public.baseURL}/field`,
    credentials: 'omit',
    onRequest({ options }) {
      if (token.value) {
        const headers = new Headers(options.headers as HeadersInit)
        headers.set('Authorization', `Field ${token.value}`)
        options.headers = headers
      }
    },
    onResponseError({ response, options }) {
      const auth = new Headers(options.headers as HeadersInit).get(
        'Authorization'
      )
      if (response.status === 401 && auth && auth === 'Field ' + token.value) {
        setSession(null, null)
        navigateTo('/field')
      }
    },
  })

  // A plain signature: Nuxt's route-typed $fetch is too deep for TypeScript with these dynamic URLs.
  const fieldFetch: FieldFetch = (url, options) =>
    rawFetch(url, options as never) as never

  const acceptLogin = (response: { token: string; session: FieldSession }) => {
    setSession(response.token, response.session)
    departmentChosen.value = response.session.allowed_departments.length <= 1
    writeStorage(CHOSEN_KEY, departmentChosen.value ? '1' : null)
    return response.session
  }

  const signIn = async (body: {
    phone_number: string
    pin: string
    personal_only?: boolean
  }) => {
    const response = await fieldFetch<{ token: string; session: FieldSession }>(
      '/login/',
      {
        method: 'POST',
        body,
      }
    )
    return acceptLogin(response)
  }

  /** Switch the department the worker is shooting for (Mitigation, Recon, Packout). */
  const setDepartment = async (department: FieldDepartment) => {
    const updated = await fieldFetch<FieldSession>('/me/', {
      method: 'PATCH',
      body: { department },
    })
    setSession(token.value, updated)
    departmentChosen.value = true
    writeStorage(CHOSEN_KEY, '1')
    return updated
  }

  /** Refresh the session from the server (departments may have changed in the office). */
  const refreshSession = async () => {
    if (!token.value) return
    try {
      const original = token.value
      const updated = await fieldFetch<FieldSession>('/me/')
      if (token.value === original) setSession(original, updated)
    } catch {
      // Offline: keep the saved session.
    }
  }

  const signOut = async () => {
    try {
      await fieldFetch('/logout/', { method: 'POST' })
    } finally {
      setSession(null, null)
    }
  }

  return {
    acceptLogin,
    departmentChosen,
    fieldFetch,
    isSignedIn,
    refreshSession,
    session,
    setDepartment,
    signIn,
    signOut,
    token,
  }
}

export const getPosition = (timeout = 15000): Promise<FieldPosition> =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error(ft('This phone does not share its location.')))
      return
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
        }),
      (error) =>
        reject(
          new Error(
            error.code === error.PERMISSION_DENIED
              ? ft(
                  'Location is blocked. Allow location for this site in your browser settings.'
                )
              : ft(
                  'Could not get your location. Try again outside or search by name.'
                )
          )
        ),
      { enableHighAccuracy: true, maximumAge: 30000, timeout }
    )
  })

// Server messages come in English; ft() shows the Spanish ones listed in locales/field-es/server.ts.
export const fieldErrorMessage = (error: unknown): string => {
  const data = (error as { data?: Record<string, unknown> })?.data
  if (data) {
    if (typeof data.detail === 'string') return ft(data.detail)
    const first = Object.values(data)[0]
    if (Array.isArray(first) && typeof first[0] === 'string')
      return ft(first[0])
  }
  if (error instanceof Error && error.message) return ft(error.message)
  return ft('Something went wrong. Check your connection and try again.')
}

export const formatDistance = (meters: number | null) => {
  if (meters === null) return ''
  const feet = meters * 3.28084
  if (feet < 1000) return `${Math.round(feet / 10) * 10} ft`
  return `${(meters / 1609.34).toFixed(1)} mi`
}

// Photos taken further than this from the job's address ask for confirmation first.
export const FAR_FROM_JOB_METERS = 300

export const distanceBetween = (
  a: { latitude: number; longitude: number },
  b: { latitude: number; longitude: number }
) => {
  const toRad = (value: number) => (value * Math.PI) / 180
  const dLat = toRad(b.latitude - a.latitude)
  const dLng = toRad(b.longitude - a.longitude)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) *
      Math.cos(toRad(b.latitude)) *
      Math.sin(dLng / 2) ** 2
  return 2 * 6371000 * Math.asin(Math.sqrt(h))
}

/** Ask before filing photos on a job that's far from where the phone is. True means go ahead. */
export const confirmFarFromJob = (
  distance: number | null | undefined,
  jobTitle: string | undefined,
  count = 1
) => {
  if (
    distance === null ||
    distance === undefined ||
    distance <= FAR_FROM_JOB_METERS
  )
    return true
  const where = jobTitle
    ? ft('You’re {distance} from this job ({job}).', {
        distance: formatDistance(distance),
        job: jobTitle,
      })
    : ft('You’re {distance} from this job.', {
        distance: formatDistance(distance),
      })
  const question =
    count === 1
      ? ft('Add the photo to this job anyway?')
      : ft('Add {count} photos to this job anyway?', { count })
  return window.confirm(`${where}\n\n${question}`)
}
